/** Helpers de data/cor e o modelo unificado de eventos do calendário. */
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  commitmentTimeLabel,
  type Absence,
  type AgendaAppointmentEvent,
  type AgendaEvents,
  type Commitment,
} from '@erp/shared';

// ── Cores ───────────────────────────────────────────────────────────────────

/** Paleta fixa de 12 cores — cor determinística por atendente (userId % 12). */
export const ATTENDANT_PALETTE = [
  '#1677FF', // azul
  '#52C41A', // verde
  '#FA8C16', // laranja
  '#EB2F96', // magenta
  '#722ED1', // roxo
  '#13C2C2', // ciano
  '#F5222D', // vermelho
  '#A0D911', // lima
  '#2F54EB', // índigo
  '#FA541C', // vulcão
  '#08979C', // petróleo
  '#D4B106', // dourado
] as const;

export function attendantColor(userId: number): string {
  return ATTENDANT_PALETTE[((userId % 12) + 12) % 12] ?? ATTENDANT_PALETTE[0];
}

/** Barra de compromisso agendado direto para um atendente (sem agenda). */
export const COMMITMENT_USER_COLOR = '#A0522D';

export function commitmentColor(c: Commitment): string {
  return c.calendarId !== null && c.calendarColor ? c.calendarColor : COMMITMENT_USER_COLOR;
}

export const ABSENCE_COLOR = '#8C8C8C';

// ── Datas ───────────────────────────────────────────────────────────────────

/** Parse de 'yyyy-MM-dd' como meia-noite LOCAL (new Date(iso) seria UTC). */
export function parseDay(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function toISODay(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

export function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** dd/mm/aaaa */
export function formatBRDate(iso: string): string {
  return format(parseDay(iso), 'dd/MM/yyyy');
}

/**
 * 'segunda-feira a quarta-feira, 11:00 até 14:00' (mesmo dia: só um dia). Com
 * período (spec 039, adendo 3), o período no lugar dos horários:
 * 'terça-feira, Tarde'.
 */
export function commitmentWhen(c: Commitment): string {
  const hours = c.period ? commitmentTimeLabel(c) : `${c.startTime} até ${c.endTime}`;
  const startDay = format(parseDay(c.startDate), 'EEEE', { locale: ptBR });
  if (c.startDate === c.endDate) return `${startDay}, ${hours}`;
  const endDay = format(parseDay(c.endDate), 'EEEE', { locale: ptBR });
  return `${startDay} a ${endDay}, ${hours}`;
}

// ── Eventos unificados ──────────────────────────────────────────────────────

export type AgendaEventItem =
  | { kind: 'appointment'; appointment: AgendaAppointmentEvent }
  | { kind: 'commitment'; commitment: Commitment }
  | { kind: 'absence'; absence: Absence };

export function eventKey(item: AgendaEventItem): string {
  switch (item.kind) {
    case 'appointment':
      return `apt-${item.appointment.id}`;
    case 'commitment':
      return `cmt-${item.commitment.id}`;
    case 'absence':
      return `abs-${item.absence.id}`;
  }
}

export interface AgendaFilters {
  showAppointments: boolean;
  showCommitments: boolean;
  showAbsences: boolean;
  /** ids DESMARCADOS (default vazio = todos visíveis) */
  excludedCalendarIds: ReadonlySet<number>;
  excludedUserIds: ReadonlySet<number>;
}

function isVisible(item: AgendaEventItem, f: AgendaFilters): boolean {
  switch (item.kind) {
    case 'appointment':
      return f.showAppointments && !f.excludedUserIds.has(item.appointment.userId);
    case 'commitment': {
      if (!f.showCommitments) return false;
      const c = item.commitment;
      if (c.calendarId !== null) return !f.excludedCalendarIds.has(c.calendarId);
      return c.assigneeUserId === null || !f.excludedUserIds.has(c.assigneeUserId);
    }
    case 'absence':
      return f.showAbsences && !f.excludedUserIds.has(item.absence.userId);
  }
}

function sortKey(item: AgendaEventItem): string {
  // Ausências (dia inteiro) primeiro; depois por hora de início.
  switch (item.kind) {
    case 'absence':
      return `0`;
    case 'commitment':
      return `1-${item.commitment.startTime}`;
    case 'appointment':
      return `1-${item.appointment.startTime}`;
  }
}

/**
 * Expande os eventos (compromissos/ausências podem atravessar vários dias)
 * num Map dia ISO → itens visíveis ordenados, restrito ao range [start..end].
 */
export function eventsByDay(
  data: AgendaEvents,
  filters: AgendaFilters,
  startISO: string,
  endISO: string
): Map<string, AgendaEventItem[]> {
  const byDay = new Map<string, AgendaEventItem[]>();
  const push = (day: string, item: AgendaEventItem) => {
    if (day < startISO || day > endISO) return;
    const list = byDay.get(day);
    if (list) list.push(item);
    else byDay.set(day, [item]);
  };
  const pushRange = (from: string, to: string, item: AgendaEventItem) => {
    const cursor = parseDay(from < startISO ? startISO : from);
    const last = to > endISO ? endISO : to;
    while (toISODay(cursor) <= last) {
      push(toISODay(cursor), item);
      cursor.setDate(cursor.getDate() + 1);
    }
  };

  for (const a of data.appointments) {
    const item: AgendaEventItem = { kind: 'appointment', appointment: a };
    if (isVisible(item, filters)) push(a.date, item);
  }
  for (const c of data.commitments) {
    const item: AgendaEventItem = { kind: 'commitment', commitment: c };
    if (isVisible(item, filters)) pushRange(c.startDate, c.endDate, item);
  }
  for (const ab of data.absences) {
    const item: AgendaEventItem = { kind: 'absence', absence: ab };
    if (isVisible(item, filters)) pushRange(ab.startDate, ab.endDate, item);
  }

  for (const list of byDay.values()) {
    list.sort((x, y) => sortKey(x).localeCompare(sortKey(y)));
  }
  return byDay;
}
